import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Modal } from "reactstrap";
import MetaTags from "react-meta-tags";
import Datatables from "pages/table/datatable";
import { tableColumns } from "./components/TableColumns";
import handleExportCSV from "pages/utils/handleExportCSV";
import { InputField, SelectField, DatePickerField } from "pages/InputFields/InputFields";
import StatsCard from "./components/StatsCard";
import FilterBar from "./components/FilterBar";
import { showSuccessAlert, showErrorAlert } from "pages/utils/Alerts/alertMessages";
import Switch from "react-switch";
import { get, post } from "helpers/api_helper";

const Offsymbol = () => (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", fontSize: 12, color: "#fff", paddingRight: 2 }}>
        No
    </div>
);

const OnSymbol = () => (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", fontSize: 12, color: "#fff", paddingRight: 2 }}>
        Yes
    </div>
);

const Notifications = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("Add");
    const [selectedData, setSelectedData] = useState(null);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [formData, setFormData] = useState({ title: "", message: "", type: null, target_audience: null, channel: null, status: "Pending", scheduled_at: "" });
    const [sendNow, setSendNow] = useState(true);
    const [notificationsList, setNotificationsList] = useState([]);

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await get("/superadmin/notifications");
            const data = res?.data || res;
            if (Array.isArray(data)) {
                const formatted = data.map((n, idx) => ({
                    id: n.id || idx + 1,
                    title: n.title || "Notification",
                    message: n.message || "",
                    type: n.type || "Promotion",
                    target_audience: n.target_audience || "All",
                    channel: n.channel || "FCM",
                    scheduled_at: n.scheduled_at ? new Date(n.scheduled_at).toLocaleString() : "—",
                    status: n.status || "Sent",
                }));
                setNotificationsList(formatted);
            }
        } catch (e) {
            console.error("Failed to load notifications from database", e);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const toggleModal = useCallback(() => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setSelectedData(null);
            setFormData({ title: "", message: "", type: null, target_audience: null, channel: null, status: "Pending", scheduled_at: "" });
        }
    }, [modalOpen]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, option) => {
        setFormData(prev => ({ ...prev, [name]: option }));
    };

    const handleDateChange = (selectedDates) => {
        setFormData(prev => ({
            ...prev,
            scheduled_at: selectedDates[0] || null
        }));
    };

    const handleSubmit = async () => {
        const { title, message, type, target_audience, channel } = formData;

        if (!title || !message || !type || !target_audience || !channel) {
            showErrorAlert("Please fill all required fields.");
            return;
        }

        try {
            const payload = {
                ...formData,
                type: type?.value || type,
                target_audience: target_audience?.value || target_audience,
                channel: channel?.value || channel,
                status: sendNow ? "Sent" : "Scheduled",
            };
            await post("/superadmin/notifications", payload);
            showSuccessAlert(`Notification ${modalMode === "Add" ? "created" : "updated"} successfully in database!`);
            fetchNotifications();
            toggleModal();
        } catch (e) {
            showErrorAlert("Failed to save notification to database.");
        }
    };

    const handleAction = (mode, data = null) => {
        setModalMode(mode);
        setSelectedData(data);

        if ((mode === "Edit" || mode === "View") && data) {
            setFormData({
                title: data.title || "",
                message: data.message || "",
                type: data.type || null,
                target_audience: data.target_audience || null,
                channel: data.channel || null,
                scheduled_at: data.scheduled_at || ""
            });
        } else {
            setFormData({ title: "", message: "", type: null, target_audience: null, channel: null, status: "Pending", scheduled_at: "" });
        }
        setModalOpen(true);
    };

    const isAnySelected = selectedRowIds.length > 0;

    return (
        <React.Fragment>
            <div className="dash-page ms-4 me-4">
                <MetaTags>
                    <title>Notifications | {process.env.REACT_APP_TITLE}</title>
                </MetaTags>
                <Container fluid>
                    <div className="p-3">
                        <Row className="align-items-center mb-4">
                            <Col md="4">
                                <h4 className="mb-0 fw-bolder">Push Notifications</h4>
                            </Col>
                            <Col md="8" className="d-flex justify-content-end gap-2">
                                {isAnySelected && (
                                    <>
                                        <button className="btn btn-danger border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                            onClick={() => handleAction("Delete")}>
                                            <i className="bx bx-trash me-1"></i> Delete ({selectedRowIds.length})
                                        </button>
                                        <button className="btn btn-success border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                            onClick={handleExportCSV}>
                                            <i className="bx bx-export me-1"></i> Export All ({selectedRowIds.length})
                                        </button>
                                    </>
                                )}
                                <button className="btn btn-primary border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                    onClick={() => handleAction("Add")}>
                                    <i className="bx bx-plus me-1"></i> New Notification
                                </button>
                            </Col>
                        </Row>

                        <StatsCard notificationsList={notificationsList} />
                        <FilterBar />

                        <div className="p-3 bg-white shadow-sm border" style={{ borderRadius: "10px" }}>
                            <Datatables
                                columns={tableColumns(handleAction)}
                                showTableOnly={true}
                                rows={notificationsList}
                                keyField={"id"}
                                isCheckbox={true}
                                setSelectedRowIds={setSelectedRowIds}
                            />
                        </div>

                        <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" className="custom-modal">
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold mb-1">{`${modalMode} ${selectedData ? selectedData.title : 'Notification'}`}</h5>
                                <button type="button" onClick={toggleModal} className="close" aria-label="Close"></button>
                            </div>

                            <div className="modal-body">
                                {modalMode === "View" ? (
                                    <div className="p-2">
                                        <div className="text-center p-3">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary h2">
                                                    {selectedData.title.charAt(0)}
                                                </span>
                                            </div>
                                            <h5 className="mb-1 fw-bolder">{selectedData.title}</h5>
                                            <p className="text-muted mt-2"><strong>Type:</strong> {selectedData.type}</p>
                                            <hr />
                                            <Row>
                                                <Col md="12" className="mb-2" ><strong>Title:</strong> {formData.channel}</Col>
                                                <Col md="12"><strong>Message:</strong> {formData.message}</Col>
                                            </Row>
                                        </div>
                                        <hr />
                                        <div className="d-flex justify-content-end">
                                            <button className="btn btn-secondary" onClick={toggleModal}>Close</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-2">
                                        <Row className="g-3">
                                            <Col md="6">
                                                <InputField label="Notification Title" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Summer Sale" />
                                            </Col>
                                            <Col md="6">
                                                <InputField label="Message" name="message" value={formData.message} onChange={handleChange} placeholder="Enter message content" />
                                            </Col>
                                            <Col md="6">
                                                <SelectField
                                                    label="Notification Type"
                                                    value={formData.type}
                                                    onChange={(opt) => handleSelectChange("type", opt)}
                                                    options={[
                                                        { value: "Promotion", label: "Promotion" },
                                                        { value: "Alert", label: "Alert" },
                                                        { value: "Update", label: "Update" },
                                                    ]}
                                                />
                                            </Col>
                                            <Col md="6">
                                                <SelectField
                                                    label="Audience"
                                                    value={formData.target_audience}
                                                    onChange={(opt) => handleSelectChange("target_audience", opt)}
                                                    options={[
                                                        { value: "All", label: "All Users" },
                                                        { value: "Agents", label: "Agents Only" },
                                                    ]}
                                                />
                                            </Col>
                                            <Col md="6">
                                                <SelectField
                                                    label="Channel"
                                                    value={formData.channel}
                                                    onChange={(opt) => handleSelectChange("channel", opt)}
                                                    options={[
                                                        { value: "FCM", label: "Mobile Push (FCM)" },
                                                        { value: "Web", label: "Web Notification" },
                                                    ]}
                                                />
                                            </Col>
                                            <Col md="6">
                                                <DatePickerField
                                                    label="Schedule Date"
                                                    onChange={handleDateChange}
                                                    options={{ dateFormat: "d M, Y", minDate: "today" }}
                                                />
                                            </Col>
                                            <Col lg="6">
                                                <h5 className="font-size-14 mb-2">Send Immediately?</h5>
                                                <Switch
                                                    uncheckedIcon={<Offsymbol />}
                                                    checkedIcon={<OnSymbol />}
                                                    onColor="#626ed4"
                                                    onChange={() => setSendNow(!sendNow)}
                                                    checked={sendNow}
                                                />
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <button className="btn btn-danger" style={{ width: "100px" }} onClick={toggleModal}>Cancel</button>
                                            <button className="btn btn-primary px-4" onClick={handleSubmit}
                                                style={{ width: `${modalMode === "Add" ? "110px" : "140px"}` }}>
                                                {modalMode === "Add" ? "Create" : "Save Changes"}</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Modal>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Notifications;